import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * Los cuatro tonos que existen en el dashboard. No hay un quinto y no hay
 * badges de color arbitrario: el color es el vocabulario de estado, y
 * ampliarlo lo diluye.
 *
 *   neutral    dato sin carga (rol, cantidad, etiqueta)
 *   progress   en curso, todavía sin resultado: pedido, generando, en cola,
 *              en tránsito. Va en violeta —el color de la interacción— y no
 *              en ámbar, porque el ámbar significa sospecha (#174). Antes
 *              "Pedido" y "Sospechosa" se pintaban igual y el día que aparecía
 *              una anomalía de verdad nadie miraba el color.
 *   verified   verificado, activado, resuelto
 *   suspect    sospecha, pendiente de revisión, degradado
 *   critical   crítico: no activado, robo, recall, clonación
 *
 * Verde, ámbar y rojo son los resultados de verificación y nada más (manual
 * §2.8). Todo lo que no es un resultado va en neutral o en progress.
 */
export type Tone = 'neutral' | 'progress' | 'verified' | 'suspect' | 'critical';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted border-border',
  progress: 'bg-accent-soft text-accent-soft-ink border-accent/30',
  verified: 'bg-verified-soft text-verified border-verified/30',
  suspect: 'bg-suspect-soft text-suspect border-suspect/30',
  critical: 'bg-critical-soft text-critical border-critical/30',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
